import { useEffect, useState, useCallback } from 'react';
import { Megaphone, Briefcase, Wrench, CalendarClock, LoaderCircle, Check, TriangleAlert } from 'lucide-react';
import { api, eur } from '../../lib/project';

const CATEGORIES = [
  { key: 'marketing', label: 'Marketing', hint: 'Campanhas, conteúdo, aquisição de leads.', Icon: Megaphone },
  { key: 'administration', label: 'Administração', hint: 'Contabilidade, gestão, seguros, serviços administrativos.', Icon: Briefcase },
  { key: 'operations_service', label: 'Operações · assistência', hint: 'Contratos técnicos, manutenção e apoio pós-venda.', Icon: Wrench },
  { key: 'events', label: 'Eventos', hint: 'Feiras, demonstrações e presença em eventos setoriais.', Icon: CalendarClock },
];

const round0 = (v) => Math.round(Number(v) || 0);

export const FixedCostsBreakdownEditor = () => {
  const [values, setValues] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/acquisition/config');
        if (cancelled) return;
        const breakdown = data.fixed_costs_breakdown_eur || {};
        setValues({
          marketing: round0(breakdown.marketing ?? 9996),
          administration: round0(breakdown.administration ?? 3000),
          operations_service: round0(breakdown.operations_service ?? 3600),
          events: round0(breakdown.events ?? 6000),
        });
      } catch {
        if (!cancelled) setError('Não foi possível carregar os custos fixos.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const persist = useCallback(async (next) => {
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      const total = Object.values(next).reduce((a, b) => a + (Number(b) || 0), 0);
      await api.put('/acquisition/config', {
        fixed_costs_breakdown_eur: next,
        fixed_costs_year_one_eur: total,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 1600);
    } catch {
      setError('Não foi possível guardar. Verifique os valores e tente novamente.');
    } finally {
      setSaving(false);
    }
  }, []);

  const change = (key, raw) => {
    const num = Math.max(0, Math.round(Number(raw) || 0));
    setValues((prev) => {
      const next = { ...prev, [key]: num };
      persist(next);
      return next;
    });
  };

  if (loading || !values) {
    return (
      <section className="fixed-costs-breakdown loading" data-testid="fixed-costs-breakdown-loading">
        <LoaderCircle size={14} className="spin" /> A carregar custos fixos anuais…
      </section>
    );
  }

  const total = Object.values(values).reduce((a, b) => a + (Number(b) || 0), 0);

  return (
    <section className="plan-group fixed-costs-breakdown" data-testid="fixed-costs-breakdown">
      <div className="plan-group-heading">
        <h3>Custos fixos anuais · ano 1</h3>
        <p>Editar aqui atualiza também o dossier de aquisição. Cada cartão é o valor anual da rubrica; o mês médio aparece por baixo.</p>
      </div>
      <div className="fixed-costs-total-row" data-testid="fixed-costs-total-row">
        <div>
          <span>Total anual estimado</span>
          <strong data-testid="fixed-costs-total">{eur(total)}</strong>
          <small>{eur(Math.round(total / 12))} / mês em média · sincronizado com o dossier de aquisição.</small>
        </div>
        <div className="fixed-costs-status" aria-live="polite">
          {saving && <><LoaderCircle size={12} className="spin" /> A guardar…</>}
          {!saving && saved && <span className="status-ok"><Check size={12} /> Guardado</span>}
          {!saving && error && <span className="status-warn"><TriangleAlert size={12} /> {error}</span>}
        </div>
      </div>
      <div className="fixed-costs-cards" data-testid="fixed-costs-cards">
        {CATEGORIES.map(({ key, label, hint, Icon }) => {
          const annual = Number(values[key]) || 0;
          const monthly = Math.round(annual / 12);
          return (
            <article key={key} className="fixed-cost-card" data-testid={`fixed-cost-card-${key}`}>
              <header>
                <Icon size={15} aria-hidden="true" />
                <span>{label}</span>
              </header>
              <label htmlFor={`fixed-cost-${key}`} className="fixed-cost-annual">
                <span>Valor anual (€)</span>
                <input
                  id={`fixed-cost-${key}`}
                  data-testid={`fixed-cost-${key}-input`}
                  type="number"
                  min="0"
                  max="10000000"
                  step="1"
                  value={annual}
                  onChange={(e) => change(key, e.target.value)}
                />
              </label>
              <p className="fixed-cost-monthly" data-testid={`fixed-cost-${key}-monthly`}>
                ≈ {eur(monthly)} / mês
              </p>
              <p className="fixed-cost-hint">{hint}</p>
            </article>
          );
        })}
      </div>
    </section>
  );
};
